import { uploadInspectionPhoto } from "./inspectionApi.js";
import { deleteUpload, listUploads, notifyQueueChanged, putUpload } from "../storage/inspectionStore.js";

export const UPLOAD_PROGRESS_EVENT = "inspection-upload-progress";

// A photo gets this many attempts before it waits for a deliberate Retry.
// Permanent answers (the server telling us the photo is too big, the wrong format,
// or not ours) never auto-retry at all.
const MAX_AUTO_ATTEMPTS = 3;
// Waits between those attempts. Before these existed MAX_AUTO_ATTEMPTS was
// decorative: a lone photo failing on a flaky link got exactly ONE attempt and then
// sat there, because the drain loop only went round again while OTHER photos were
// succeeding — so the retry cadence of a failing photo depended on its neighbours,
// and a photo failing on its own waited for an online/visibility event or a tap that
// might never come. Real waits, so a passing 4G dropout heals itself.
const RETRY_DELAYS_MS = [2000, 8000, 30000];
// 401 is NOT in here. An expired session is the most likely failure on a phone
// that has been in a pocket since yesterday, and it is not the photo's fault:
// parking it permanently meant the day's work sat there until someone noticed the
// badge and tapped Retry. It gets its own status and is re-armed on the next pass
// (by then the inspector has usually signed in again).
const PERMANENT_STATUSES = new Set([400, 403, 409, 413, 415]);
const NEEDS_AUTH = "needs_auth";
const PROGRESS_THROTTLE_MS = 100;

let activeRun = null;
let rerunRequested = false;

// Photos picked together land in the same millisecond, so createdAt alone can't
// order them. This keeps them uploading in the order they were taken.
let queueSequence = 0;

// Live progress is deliberately NOT written to IndexedDB. Persisting a number
// that changes dozens of times a second meant every tick rewrote the record and
// woke every listener, which re-read the queue (blobs and all) mid-upload.
const progressById = new Map();
const lastPublishedAt = new Map();

export function uploadProgressOf(id) {
  return progressById.has(id) ? progressById.get(id) : null;
}

function publishProgress(id, percent, { force = false } = {}) {
  progressById.set(id, percent);
  const now = Date.now();
  if (!force && now - (lastPublishedAt.get(id) || 0) < PROGRESS_THROTTLE_MS) return;
  lastPublishedAt.set(id, now);
  window.dispatchEvent(new CustomEvent(UPLOAD_PROGRESS_EVENT, { detail: { id, percent } }));
}

function clearProgress(id) {
  progressById.delete(id);
  lastPublishedAt.delete(id);
}

// crypto.randomUUID() only exists in a secure context. Testing the workspace on
// a phone means http://<lan-ip>:5173, which is NOT secure, so the queue would
// have thrown on the very first photo. getRandomValues is available either way.
function newUploadId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10x
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function errorMessage(error) {
  if (!navigator.onLine) return "Waiting for a connection.";
  // The server's own words for a 401 are "Not authenticated", which tells an
  // inspector nothing about what to do next.
  if (error.response?.status === 401) return "Your session has expired. Sign in again.";
  return error.response?.data?.error || error.message || "Upload failed. Tap Retry.";
}

function isPermanent(error) {
  return PERMANENT_STATUSES.has(error.response?.status);
}

export async function queuePhoto({ file, userId, inspectionId }) {
  const record = {
    id: newUploadId(),
    userId: Number(userId),
    inspectionId: Number(inspectionId),
    filename: file.name || `inspection-${Date.now()}.jpg`,
    contentType: file.type || "application/octet-stream",
    blob: file,
    capturedAt: new Date(file.lastModified || Date.now()).toISOString(),
    createdAt: new Date().toISOString(),
    sequence: (queueSequence += 1),
    status: "waiting",
    attempts: 0,
    error: "",
  };
  await putUpload(record);
  return record;
}

export async function discardQueuedPhoto(id) {
  clearProgress(id);
  await deleteUpload(id);
}

// Reset failed photos so the next pass picks them up — a manual Retry means the
// inspector is telling us the reason for the failure has gone away.
export async function retryFailedUploads(userId) {
  const rows = await listUploads(userId);
  for (const row of rows) {
    if (row.status !== "needs_retry" && row.status !== NEEDS_AUTH) continue;
    await putUpload({ ...row, status: "waiting", attempts: 0, error: "" });
  }
  return processUploadQueue(userId);
}

// Tidy up before a run.
//
// needs_auth: re-armed, so an online/visibility kick after the inspector signs in
// again picks the day's photos back up on its own. If the session is still dead the
// first upload parks them again — one wasted request, not a lost afternoon.
//
// uploading: nothing can be mid-flight at the start of a run (processUploadQueue
// serialises them), so a row still marked uploading is from an app the phone killed
// while it was sending. It was rendering as "Uploading" with a live progress bar
// that would never move.
async function reviveInterrupted(userId) {
  for (const row of await listUploads(userId)) {
    if (row.status !== NEEDS_AUTH && row.status !== "uploading") continue;
    await putUpload({ ...row, status: "waiting", attempts: 0, error: "" });
  }
}

async function uploadOne(row) {
  const uploading = { ...row, status: "uploading", error: "", attempts: row.attempts + 1 };
  await putUpload(uploading);
  publishProgress(row.id, 0, { force: true });
  try {
    await uploadInspectionPhoto(row.inspectionId, uploading, (event) => {
      const total = event.total || 0;
      publishProgress(row.id, total ? Math.round((event.loaded / total) * 100) : null);
    });
    clearProgress(row.id);
    await deleteUpload(row.id);
    return { ok: true };
  } catch (error) {
    clearProgress(row.id);
    await putUpload({
      ...uploading,
      status: error.response?.status === 401 ? NEEDS_AUTH : "needs_retry",
      // A permanent answer is parked immediately; retrying it just burns battery.
      attempts: isPermanent(error) ? MAX_AUTO_ATTEMPTS : uploading.attempts,
      error: errorMessage(error),
    });
    return { ok: false, error };
  }
}

async function drainOnce(userId) {
  const rows = await listUploads(userId);
  let uploaded = 0;
  for (const row of rows) {
    if (!navigator.onLine) break;
    // Parked until someone taps Retry.
    if (row.status === "needs_retry" && row.attempts >= MAX_AUTO_ATTEMPTS) continue;
    const result = await uploadOne(row);
    if (result.ok) {
      uploaded += 1;
      continue;
    }
    // A signed-out or forbidden session will reject the rest of the queue too.
    if ([401, 403].includes(result.error.response?.status)) break;
  }
  return { uploaded };
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// How long before the next pass is worth running: the shortest backoff owed to any
// photo that is still allowed an automatic attempt. null = nothing left to wait for,
// so the run is finished.
async function nextRetryDelay(userId) {
  const waits = (await listUploads(userId))
    .filter((row) => row.status === "needs_retry" && row.attempts < MAX_AUTO_ATTEMPTS)
    .map((row) => RETRY_DELAYS_MS[Math.min(row.attempts, RETRY_DELAYS_MS.length - 1)]);
  return waits.length ? Math.min(...waits) : null;
}

// Keep passing over the queue while photos are still moving. The old single-pass
// version read the queue ONCE at the start, so photos added while a pass was in
// flight — picking three at once, where the first one started a run — sat there
// until the next online or visibility event happened to kick the queue.
//
// A pass that moves nothing is not the end: anything still owed an automatic
// attempt gets its backoff waited out here, which is what makes MAX_AUTO_ATTEMPTS
// mean what it says. Every pass increments attempts, so this always terminates.
async function drain(userId) {
  let uploaded = 0;
  while (navigator.onLine) {
    const pass = await drainOnce(userId);
    uploaded += pass.uploaded;
    if (pass.uploaded > 0) continue;
    const wait = await nextRetryDelay(userId);
    if (wait == null) break;
    await sleep(wait);
  }
  return { uploaded };
}

export function processUploadQueue(userId) {
  if (activeRun) {
    // Don't hand back a run that already read its work list — ask it to go round
    // again so anything queued since is included.
    rerunRequested = true;
    return activeRun;
  }
  activeRun = (async () => {
    let uploaded = 0;
    await reviveInterrupted(userId);
    do {
      rerunRequested = false;
      uploaded += (await drain(userId)).uploaded;
    } while (rerunRequested && navigator.onLine);
    return { uploaded };
  })().finally(() => {
    activeRun = null;
    rerunRequested = false;
    notifyQueueChanged();
  });
  return activeRun;
}
